import { useEffect, useMemo, useState } from 'react';
import {
  Loader2,
  Sparkles,
  Clock,
  Briefcase,
  CheckCircle2,
  AlertTriangle,
} from 'lucide-react';
import apiClient from '@/lib/api';
import { usePermissions } from '@/contexts/PermissionContext';
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from '@easyflow/ui-frontend/components/ui/sheet';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@easyflow/ui-frontend/components/ui/tabs';
import { Badge } from '@easyflow/ui-frontend/components/ui/badge';
import { Button } from '@easyflow/ui-frontend/components/ui/button';
import { useToast } from '@/hooks/use-toast';
import { cn, getErrorMessage } from '@/lib/utils';

type SkillLevel = 'base' | 'intermedio' | 'avanzato' | 'esperto';

const LEVELS: SkillLevel[] = ['base', 'intermedio', 'avanzato', 'esperto'];
const LEVEL_LABEL: Record<SkillLevel, string> = {
  base: 'Base',
  intermedio: 'Intermedio',
  avanzato: 'Avanzato',
  esperto: 'Esperto',
};
const LEVEL_RANK: Record<SkillLevel, number> = {
  base: 1,
  intermedio: 2,
  avanzato: 3,
  esperto: 4,
};

interface SkillRow { id: string; name: string; color: string | null }
interface UserSkillRow { user_id: string; skill_id: string; skill_level: SkillLevel | null }
interface ServiceSkillRow {
  product_id: string;
  product_name: string;
  service_type_id: string | null;
  skill_id: string | null;
  min_skill_level: SkillLevel | null;
}
interface SkillMatrixData {
  skills: SkillRow[];
  users: { id: string; first_name: string; last_name: string }[];
  user_skills: UserSkillRow[];
  service_skills: ServiceSkillRow[];
}

interface ShiftRowApi {
  date: string;
  shifts: Record<string, {
    shift_name: string;
    shift_code: string;
    start_time: string;
    end_time: string;
    shift_color: string;
    is_overnight: boolean;
    store_name: string;
    store_city: string;
    people: Array<{ user_id: string }>;
  }>;
}

export interface PlanningSkillDrawerProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  userId: string | null;
  userName: string | null;
  /** Caller access, additionally checked against the HR backend permission. */
  canEdit: boolean;
}

function normalizeLevel(v: string | null | undefined): SkillLevel {
  if (v === 'intermedio' || v === 'avanzato' || v === 'esperto' || v === 'base') return v;
  return 'base';
}

function fmtDate(d: string | Date): string {
  const dt = typeof d === 'string' ? new Date(d) : d;
  return dt.toLocaleDateString('it-IT', { weekday: 'short', day: '2-digit', month: 'short' });
}

export default function PlanningSkillDrawer({
  open,
  onOpenChange,
  userId,
  userName,
  canEdit: callerCanEdit,
}: PlanningSkillDrawerProps) {
  const { hasPermission } = usePermissions();
  const canEdit = callerCanEdit && hasPermission('hr.settings.manage');
  const { toast } = useToast();
  const [matrix, setMatrix] = useState<SkillMatrixData | null>(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  // local map skill_id -> level (or 'none' for not assigned)
  const [editLevels, setEditLevels] = useState<Map<string, SkillLevel | 'none'>>(new Map());
  const [shiftsByDate, setShiftsByDate] = useState<Record<string, ShiftRowApi['shifts']>>({});
  const [shiftsLoading, setShiftsLoading] = useState(false);

  useEffect(() => {
    if (!open || !userId) return;
    let cancelled = false;
    setLoading(true);
    apiClient.get<SkillMatrixData>('/calendar/skill-matrix')
      .then(r => {
        if (cancelled) return;
        if (r.success && r.data) {
          setMatrix(r.data);
          const map = new Map<string, SkillLevel | 'none'>();
          for (const s of r.data.skills) map.set(s.id, 'none');
          for (const us of r.data.user_skills) {
            if (us.user_id === userId) {
              map.set(us.skill_id, normalizeLevel(us.skill_level));
            }
          }
          setEditLevels(map);
        }
      })
      .catch(err => {
        if (!cancelled) toast({ title: 'Errore', description: err.message || 'Caricamento skill fallito', variant: 'destructive' });
      })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [open, userId, toast]);

  // Load 7-day shift window once drawer opens
  useEffect(() => {
    if (!open || !userId) return;
    let cancelled = false;
    setShiftsLoading(true);
    const today = new Date();
    const end = new Date(today.getTime() + 7 * 24 * 60 * 60 * 1000);
    const fmt = (d: Date) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
    apiClient.get<Record<string, ShiftRowApi['shifts']>>(
      `/hr/shifts/by-date?date_from=${fmt(today)}&date_to=${fmt(end)}`
    ).then(r => {
      if (cancelled) return;
      if (r.success && r.data) {
        setShiftsByDate(r.data || {});
      } else {
        setShiftsByDate({});
      }
    }).catch(() => {
      if (!cancelled) setShiftsByDate({});
    }).finally(() => { if (!cancelled) setShiftsLoading(false); });
    return () => { cancelled = true; };
  }, [open, userId]);

  const myShifts = useMemo(() => {
    if (!userId) return [];
    const out: { date: string; shift: ShiftRowApi['shifts'][string] }[] = [];
    for (const [date, shifts] of Object.entries(shiftsByDate)) {
      for (const shift of Object.values(shifts || {})) {
        if (shift.people?.some(p => p.user_id === userId)) {
          out.push({ date, shift });
        }
      }
    }
    out.sort((a, b) => a.date.localeCompare(b.date));
    return out;
  }, [shiftsByDate, userId]);

  // Aggregate services with their requirements + per-user readiness
  const servicesWithReadiness = useMemo(() => {
    if (!matrix) return [];
    const byProduct = new Map<string, { id: string; name: string; required: { skill_id: string; name: string; min: SkillLevel }[] }>();
    const skillNameById = new Map(matrix.skills.map(s => [s.id, s.name]));
    for (const ss of matrix.service_skills) {
      const p = byProduct.get(ss.product_id) || { id: ss.product_id, name: ss.product_name, required: [] };
      if (ss.skill_id && ss.min_skill_level) {
        p.required.push({
          skill_id: ss.skill_id,
          name: skillNameById.get(ss.skill_id) || ss.skill_id,
          min: normalizeLevel(ss.min_skill_level),
        });
      }
      byProduct.set(ss.product_id, p);
    }
    const out: {
      id: string;
      name: string;
      required: { skill_id: string; name: string; min: SkillLevel }[];
      missing: string[];
      under: string[];
      ready: boolean;
    }[] = [];
    for (const p of byProduct.values()) {
      const missing: string[] = [];
      const under: string[] = [];
      for (const req of p.required) {
        const lvl = editLevels.get(req.skill_id);
        if (!lvl || lvl === 'none') missing.push(req.name);
        else if (LEVEL_RANK[lvl] < LEVEL_RANK[req.min]) under.push(`${req.name} (${LEVEL_LABEL[lvl]} < ${LEVEL_LABEL[req.min]})`);
      }
      out.push({
        ...p,
        missing,
        under,
        ready: missing.length === 0 && under.length === 0,
      });
    }
    out.sort((a, b) => a.name.localeCompare(b.name));
    return out;
  }, [matrix, editLevels]);

  async function handleSave() {
    if (!canEdit) return;
    if (!userId || !matrix) return;
    const payload = Array.from(editLevels.entries())
      .filter(([, lvl]) => lvl !== 'none')
      .map(([skill_id, lvl]) => ({ skill_id, skill_level: lvl as SkillLevel }));
    setSaving(true);
    try {
      const r = await apiClient.put(`/hr/users/${userId}/skills`, { skills: payload });
      if (!r.success) throw new Error(r.error || 'Salvataggio fallito');
      toast({ title: 'Aggiornato', description: 'Competenze salvate' });
    } catch (e) {
      toast({ title: 'Errore', description: getErrorMessage(e) || 'Salvataggio fallito', variant: 'destructive' });
    } finally {
      setSaving(false);
    }
  }

  function setLevel(skillId: string, lvl: SkillLevel | 'none') {
    setEditLevels(prev => {
      const next = new Map(prev);
      next.set(skillId, lvl);
      return next;
    });
  }

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="right" className="w-full sm:max-w-lg overflow-y-auto">
        <SheetHeader>
          <SheetTitle>{userName || 'Risorsa'}</SheetTitle>
          <SheetDescription>
            Skill, turni e servizi compatibili.
          </SheetDescription>
        </SheetHeader>

        <Tabs defaultValue="skills" className="mt-4">
          <TabsList className="w-full grid grid-cols-3">
            <TabsTrigger value="skills">
              <Sparkles className="w-4 h-4 mr-1" /> Skill
            </TabsTrigger>
            <TabsTrigger value="shifts">
              <Clock className="w-4 h-4 mr-1" /> Turni
            </TabsTrigger>
            <TabsTrigger value="services">
              <Briefcase className="w-4 h-4 mr-1" /> Servizi
            </TabsTrigger>
          </TabsList>

          <TabsContent value="skills" className="mt-4">
            {loading ? (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="w-5 h-5 animate-spin text-gray-400" />
              </div>
            ) : !matrix ? (
              <div className="text-sm text-gray-500">Dati non disponibili.</div>
            ) : matrix.skills.length === 0 ? (
              <div className="text-sm text-gray-500">Nessuna skill censita nel tenant.</div>
            ) : (
              <div className="space-y-2">
                {matrix.skills.map(skill => {
                  const lvl = editLevels.get(skill.id) || 'none';
                  return (
                    <div key={skill.id} className="flex items-center justify-between gap-2 p-2 rounded border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900">
                      <div className="flex items-center gap-2 min-w-0">
                        <span
                          className="w-2 h-2 rounded-full shrink-0"
                          style={{ backgroundColor: skill.color || '#6366f1' }}
                        />
                        <span className="text-sm font-medium text-gray-900 dark:text-gray-100 truncate">
                          {skill.name}
                        </span>
                      </div>
                      {canEdit ? (
                        <select
                          value={lvl}
                          onChange={(e) => setLevel(skill.id, e.target.value as SkillLevel | 'none')}
                          className="text-xs px-2 py-1 rounded border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-200"
                        >
                          <option value="none">— non assegnata —</option>
                          {LEVELS.map(l => (
                            <option key={l} value={l}>{LEVEL_LABEL[l]}</option>
                          ))}
                        </select>
                      ) : (
                        <Badge className={cn(
                          'text-xs',
                          lvl === 'none'
                            ? 'bg-gray-100 dark:bg-gray-800 text-gray-500'
                            : 'bg-indigo-100 dark:bg-indigo-900/30 text-indigo-700 dark:text-indigo-300'
                        )}>
                          {lvl === 'none' ? 'non assegnata' : LEVEL_LABEL[lvl]}
                        </Badge>
                      )}
                    </div>
                  );
                })}
                {canEdit && (
                  <div className="pt-2 flex justify-end">
                    <Button onClick={handleSave} disabled={saving} size="sm">
                      {saving && <Loader2 className="w-3 h-3 animate-spin mr-1" />}
                      Salva competenze
                    </Button>
                  </div>
                )}
              </div>
            )}
          </TabsContent>

          <TabsContent value="shifts" className="mt-4">
            {shiftsLoading ? (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="w-5 h-5 animate-spin text-gray-400" />
              </div>
            ) : myShifts.length === 0 ? (
              <div className="text-sm text-gray-500">Nessun turno pianificato nei prossimi 7 giorni.</div>
            ) : (
              <div className="space-y-2">
                {myShifts.map(({ date, shift }, i) => (
                  <div key={i} className="p-2 rounded border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900">
                    <div className="flex items-center justify-between">
                      <div className="text-sm font-medium text-gray-900 dark:text-gray-100">
                        {fmtDate(date)}
                      </div>
                      <Badge
                        className="text-[10px]"
                        style={{ backgroundColor: shift.shift_color + '20', color: shift.shift_color, borderColor: shift.shift_color }}
                      >
                        {shift.shift_code || shift.shift_name}
                      </Badge>
                    </div>
                    <div className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                      {shift.start_time?.slice(0, 5)} – {shift.end_time?.slice(0, 5)}
                      {shift.is_overnight && ' (notturno)'}
                      {shift.store_name && ` · ${shift.store_name}`}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </TabsContent>

          <TabsContent value="services" className="mt-4">
            {loading ? (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="w-5 h-5 animate-spin text-gray-400" />
              </div>
            ) : servicesWithReadiness.length === 0 ? (
              <div className="text-sm text-gray-500">Nessun servizio configurato.</div>
            ) : (
              <div className="space-y-2">
                {servicesWithReadiness.map(svc => (
                  <div key={svc.id} className="p-2 rounded border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900">
                    <div className="flex items-center justify-between">
                      <div className="text-sm font-medium text-gray-900 dark:text-gray-100 truncate">
                        {svc.name}
                      </div>
                      {svc.ready ? (
                        <Badge className="bg-emerald-100 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-300 border border-emerald-300 dark:border-emerald-700 text-[10px] inline-flex items-center gap-1">
                          <CheckCircle2 className="w-3 h-3" /> Idoneo
                        </Badge>
                      ) : (
                        <Badge className="bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-300 border border-amber-300 dark:border-amber-700 text-[10px] inline-flex items-center gap-1">
                          <AlertTriangle className="w-3 h-3" /> Skill mancanti
                        </Badge>
                      )}
                    </div>
                    {svc.required.length === 0 ? (
                      <div className="text-[11px] text-gray-500 dark:text-gray-400 mt-1">
                        Nessuna skill richiesta.
                      </div>
                    ) : (
                      <div className="text-[11px] text-gray-500 dark:text-gray-400 mt-1 space-y-0.5">
                        <div>
                          Richiede: {svc.required.map(r => `${r.name} (${LEVEL_LABEL[r.min]})`).join(', ')}
                        </div>
                        {svc.missing.length > 0 && (
                          <div className="text-rose-600 dark:text-rose-400">
                            Mancano: {svc.missing.join(', ')}
                          </div>
                        )}
                        {svc.under.length > 0 && (
                          <div className="text-amber-600 dark:text-amber-400">
                            Sotto livello: {svc.under.join(', ')}
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </TabsContent>
        </Tabs>
      </SheetContent>
    </Sheet>
  );
}
